import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { useEffect, useRef } from 'react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import type { InvoiceSectionPickerUiOptions, InvoiceSection } from './schema';
import { useInvoiceSections } from './useInvoiceSections';

export type { InvoiceSectionPickerSchema } from './schema';

type Props = FieldExtensionComponentProps<
  string | undefined,
  InvoiceSectionPickerUiOptions
>;

function getOptionLabel(option: InvoiceSection): string {
  return option.displayName || option.id;
}

function getOptionSelected(
  option: InvoiceSection,
  value: InvoiceSection,
): boolean {
  return option.id === value.id;
}

export const InvoiceSectionPicker = (props: Props) => {
  const {
    onChange,
    schema: {
      title = 'Invoice Section',
      description = 'Select an invoice section',
    },
    required,
    uiSchema,
    rawErrors,
    formData,
    formContext,
  } = props;

  const uiOptions = uiSchema['ui:options'];
  const selectedBillingProfile = uiOptions?.billingProfileField
    ? String(formContext?.formData?.[uiOptions.billingProfileField] ?? '')
    : uiOptions?.billingProfileId ?? '';
  const billingProfileId =
    uiOptions?.billingProfileIdMap?.[selectedBillingProfile] ??
    selectedBillingProfile;
  const { invoiceSections, loading, error } =
    useInvoiceSections(billingProfileId);

  const previousBillingProfileId = useRef(billingProfileId);
  useEffect(() => {
    if (previousBillingProfileId.current !== billingProfileId) {
      previousBillingProfileId.current = billingProfileId;
      onChange(undefined);
    }
  }, [billingProfileId, onChange]);

  const selectedOption = invoiceSections.find(s => s.id === formData) ?? null;

  const handleChange = (
    _: React.ChangeEvent<{}>,
    value: InvoiceSection | null,
  ) => {
    onChange(value?.id ?? undefined);
  };

  if (!billingProfileId) {
    return (
      <Typography color="error" variant="body2">
        Select a billing profile to load invoice sections.
      </Typography>
    );
  }

  return (
    <Autocomplete
      options={invoiceSections}
      getOptionLabel={getOptionLabel}
      getOptionSelected={getOptionSelected}
      value={selectedOption}
      onChange={handleChange}
      loading={loading}
      disableClearable={false}
      freeSolo={false}
      renderInput={params => (
        <TextField
          {...params}
          label={title}
          helperText={error ?? (rawErrors?.length ? rawErrors[0] : description)}
          error={!!rawErrors?.length || !!error}
          required={required}
          variant="outlined"
          InputProps={{
            ...params.InputProps,
          }}
        />
      )}
    />
  );
};
